import { useAuth } from "@vellum/micro-app-auth";
import { Alert, Button, ConfirmDialog } from "@vellum/ui";
import {
  UserRoles,
  type HubFolderItem,
  type HubItemsReorderRequest,
  type LinkSchema,
  type LinkSection,
} from "@vellum/vellum-api";
import { useCallback, useEffect, useState } from "react";

import { vellumApiSdk } from "@vellum/micro-app-auth";
import { EditableFolderCard, FolderCard } from "@/components/FolderCard";
import { FolderDialog } from "@/components/FolderDialog";
import {
  FolderFormDialog,
  type FolderFormValues,
} from "@/components/FolderFormDialog";
import { AddCard, EditableLinkCard, LinkCard } from "@/components/LinkCard";
import {
  LinkFormDialog,
  type LinkFormValues,
} from "@/components/LinkFormDialog";
import {
  SECTION_ORDER,
  folderOrderBody,
  folders,
  itemsInSection,
  sameItem,
  sectionsOrderBody,
  withFolderLinkMoved,
  withItemMoved,
  type HubItem,
  type HubItemRef,
} from "@/lib/hubItems";
// The configured SDK, not @vellum/vellum-api directly — importing this module
// is what points the client at the API origin with credentials.

type LinkFormTarget = {
  link: LinkSchema | null;
  section: LinkSection;
  folderId: string | null;
};
type FolderFormTarget = { folder: HubFolderItem | null; section: LinkSection };
type DeleteTarget =
  | { kind: "link"; link: LinkSchema }
  | { kind: "folder"; folder: HubFolderItem };

function getTimeOfDayGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) {
    return "Good morning";
  } else if (hour < 18) {
    return "Good afternoon";
  } else {
    return "Good evening";
  }
}

function deleteTitle(target: DeleteTarget | null) {
  if (!target) {
    return "Delete this item?";
  }
  const title =
    target.kind === "link" ? target.link.title : target.folder.title;
  return `Delete ${title}?`;
}

export function HomePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRoles.ADMIN;

  const [items, setItems] = useState<HubItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [managing, setManaging] = useState(false);
  const [linkForm, setLinkForm] = useState<LinkFormTarget | null>(null);
  const [folderForm, setFolderForm] = useState<FolderFormTarget | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [dragged, setDragged] = useState<HubItemRef | null>(null);
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);
  const [writeFailed, setWriteFailed] = useState(false);

  // Looked up from `items` so the open dialog follows every write.
  const openFolder =
    folders(items).find((folder) => folder.id === openFolderId) ?? null;

  const loadItems = useCallback(async () => {
    const result = await vellumApiSdk.listHubItems();
    setItems(result.data ?? []);
    setFailed(result.data === undefined);
  }, []);

  useEffect(() => {
    let active = true;

    void (async () => {
      try {
        await loadItems();
      } catch {
        if (active) {
          setFailed(true);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [loadItems]);

  /**
   * Runs a create, update or delete, then reloads the list. The generated SDK
   * resolves with an `error` instead of throwing, so a failed write is a value
   * to check as well as an exception to catch. Returns whether it saved.
   */
  const runWrite = async (
    write: () => Promise<{ error?: unknown }>,
  ): Promise<boolean> => {
    try {
      const result = await write();
      if (result.error !== undefined) {
        setWriteFailed(true);
        return false;
      }
      await loadItems();
      setWriteFailed(false);
      return true;
    } catch {
      setWriteFailed(true);
      return false;
    }
  };

  const submitLink = async (values: LinkFormValues) => {
    const body = {
      title: values.title.trim(),
      url: values.url.trim(),
      description: values.description.trim() || null,
      section: values.section,
      icon: values.icon,
      visible_to_roles: values.visible_to_roles,
      folder_id: values.folder_id || null,
    };
    const editing = linkForm?.link;
    const saved = await runWrite(() =>
      editing
        ? vellumApiSdk.updateLink({ body, path: { link_id: editing.id } })
        : vellumApiSdk.createLink({ body }),
    );
    if (saved) {
      setLinkForm(null);
    }
  };

  const submitFolder = async (values: FolderFormValues) => {
    const body = {
      title: values.title.trim(),
      description: values.description.trim() || null,
      section: values.section,
      icon: values.icon,
      visible_to_roles: values.visible_to_roles,
    };
    const editing = folderForm?.folder;
    const saved = await runWrite(() =>
      editing
        ? vellumApiSdk.updateLinkFolder({
            body,
            path: { folder_id: editing.id },
          })
        : vellumApiSdk.createLinkFolder({ body }),
    );
    if (saved) {
      setFolderForm(null);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }

    setDeleting(true);
    const saved = await runWrite(() =>
      pendingDelete.kind === "link"
        ? vellumApiSdk.deleteLink({
            path: { link_id: pendingDelete.link.id },
          })
        : vellumApiSdk.deleteLinkFolder({
            path: { folder_id: pendingDelete.folder.id },
          }),
    );
    setDeleting(false);
    if (saved) {
      if (pendingDelete.kind === "folder") {
        setOpenFolderId(null);
      }
      setPendingDelete(null);
    }
  };

  const persistOrder = async (
    reordered: HubItem[],
    body: HubItemsReorderRequest,
  ) => {
    const previous = items;
    // Optimistic: the grid follows the card the admin just dropped, and only
    // snaps back if the server rejects the new order.
    setItems(reordered);

    try {
      const result = await vellumApiSdk.reorderHubItems({ body });
      if (result.error !== undefined || !result.data) {
        setItems(previous);
        setWriteFailed(true);
        return;
      }

      setItems(result.data);
      setWriteFailed(false);
    } catch {
      setItems(previous);
      setWriteFailed(true);
    }
  };

  const moveItem = (section: LinkSection, targetIndex: number) => {
    if (!dragged) {
      return;
    }
    const reordered = withItemMoved(items, dragged, section, targetIndex);
    void persistOrder(reordered, sectionsOrderBody(reordered));
    setDragged(null);
  };

  const handleDropOnItem = (target: HubItem) => {
    if (!dragged || sameItem(target, dragged)) {
      setDragged(null);
      return;
    }
    const targetIndex = itemsInSection(items, target.section).findIndex(
      (item) => sameItem(item, target),
    );
    moveItem(target.section, targetIndex);
  };

  const handleDropOnSection = (section: LinkSection) =>
    moveItem(section, itemsInSection(items, section).length);

  const moveFolderLink = (draggedId: string, targetIndex: number) => {
    if (!openFolder) {
      return;
    }
    const reordered = withFolderLinkMoved(
      items,
      openFolder.id,
      draggedId,
      targetIndex,
    );
    void persistOrder(reordered, folderOrderBody(reordered, openFolder.id));
  };

  const dragProps = (item: HubItem) => ({
    dragging: dragged !== null && sameItem(item, dragged),
    onDragStart: () => setDragged({ kind: item.kind, id: item.id }),
    onDragEnd: () => setDragged(null),
    onDropOn: () => handleDropOnItem(item),
  });

  const renderItem = (item: HubItem) => {
    if (item.kind === "folder") {
      return managing ? (
        <EditableFolderCard
          key={`folder-${item.id}`}
          folder={item}
          onOpen={() => setOpenFolderId(item.id)}
          onEdit={() => setFolderForm({ folder: item, section: item.section })}
          onDelete={() => setPendingDelete({ kind: "folder", folder: item })}
          {...dragProps(item)}
        />
      ) : (
        <FolderCard
          key={`folder-${item.id}`}
          folder={item}
          onOpen={() => setOpenFolderId(item.id)}
        />
      );
    }
    return managing ? (
      <EditableLinkCard
        key={`link-${item.id}`}
        link={item}
        onEdit={() =>
          setLinkForm({ link: item, section: item.section, folderId: null })
        }
        onDelete={() => setPendingDelete({ kind: "link", link: item })}
        {...dragProps(item)}
      />
    ) : (
      <LinkCard key={`link-${item.id}`} link={item} />
    );
  };

  // Outside manage mode, empty sections are simply not rendered; inside it,
  // every section stays on screen so admins always have the "Add" cards.
  const sections = SECTION_ORDER.map((section) => ({
    section,
    items: itemsInSection(items, section),
  })).filter((group) => managing || group.items.length > 0);

  return (
    <div>
      <div className="mb-2 flex items-start justify-between gap-4">
        <h1 className="text-xl md:text-3xl font-semibold tracking-tight text-green-700">
          {getTimeOfDayGreeting()}, {user?.name.split(" ")[0] ?? ""}!
        </h1>
        {isAdmin && !loading && !failed ? (
          <div className="flex gap-2">
            <Button
              variant={managing ? "primary" : "outline"}
              onClick={() => setManaging((current) => !current)}
            >
              {managing ? "Done editing" : "Edit links"}
            </Button>
          </div>
        ) : null}
      </div>

      {writeFailed ? (
        <Alert>
          That change did not save. Check your connection and try again.
        </Alert>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500">Loading your apps…</p>
      ) : failed ? (
        <p className="text-sm text-gray-500">
          We could not load your apps just now. Refresh the page to try again.
        </p>
      ) : sections.length === 0 ? (
        <p className="text-sm text-gray-500">
          There are no apps available to you yet.
        </p>
      ) : (
        <div className="flex flex-col gap-8 mt-4">
          {sections.map(({ section, items: sectionItems }) => (
            <section
              key={section}
              onDragOver={(event) =>
                managing ? event.preventDefault() : undefined
              }
              onDrop={() =>
                managing ? handleDropOnSection(section) : undefined
              }
            >
              <h2 className="ml-1 mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {section}
              </h2>
              <div className="grid grid-cols-2 gap-1 md:gap-2 lg:gap-6 sm:grid-cols-3 lg:grid-cols-4">
                {sectionItems.map(renderItem)}
                {managing ? (
                  <>
                    <AddCard
                      label={`Add link to ${section} section`}
                      icon="plus"
                      onClick={() =>
                        setLinkForm({ link: null, section, folderId: null })
                      }
                    />
                    <AddCard
                      label={`Add folder to ${section} section`}
                      icon="folder-plus"
                      onClick={() => setFolderForm({ folder: null, section })}
                    />
                  </>
                ) : null}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Steps aside while a form or confirmation opened from it is up, and
          comes back when that closes. */}
      <FolderDialog
        folder={openFolder}
        isOpen={openFolder !== null && !linkForm && !pendingDelete}
        onClose={() => setOpenFolderId(null)}
        managing={managing}
        onAddLink={(folder) =>
          setLinkForm({
            link: null,
            section: folder.section,
            folderId: folder.id,
          })
        }
        onEditLink={(link) =>
          setLinkForm({ link, section: link.section, folderId: null })
        }
        onDeleteLink={(link) => setPendingDelete({ kind: "link", link })}
        onMoveLink={moveFolderLink}
      />

      {linkForm ? (
        <LinkFormDialog
          isOpen
          link={linkForm.link}
          defaultSection={linkForm.section}
          defaultFolderId={linkForm.folderId}
          folders={folders(items)}
          onClose={() => setLinkForm(null)}
          onSubmit={submitLink}
        />
      ) : null}

      {folderForm ? (
        <FolderFormDialog
          isOpen
          folder={folderForm.folder}
          defaultSection={folderForm.section}
          onClose={() => setFolderForm(null)}
          onSubmit={submitFolder}
        />
      ) : null}

      <ConfirmDialog
        variant="delete"
        isOpen={pendingDelete !== null}
        loading={deleting}
        title={deleteTitle(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      >
        <p className="mt-2 text-sm text-gray-600">
          {pendingDelete?.kind === "folder"
            ? `Its links move back to the ${pendingDelete.folder.section} section. None of them are deleted.`
            : "This link will stop appearing on the hub for everyone immediately."}
        </p>
      </ConfirmDialog>
    </div>
  );
}
